import MuiInput from "@mui/material/Input";
import { isValidElement } from "react";
import FormControl from "@mui/material/FormControl";
import InputAdornment from "@mui/material/InputAdornment";
import FormFieldError from "components-redesign/FormFieldError";
import FormFieldHelperText from "components-redesign/FormFieldHelperText";
import FormFieldWarning from "components-redesign/FormFieldWarning";
import InputLabel from "components-redesign/InputLabel";
import { FIGMA_COLORS, commonClasses } from "components-redesign/styles";
import { COLORS_TO_BE_REVIEWED } from "components-redesign/styles/colors";
import { UnderlineTooltipDecorator } from "components-redesign/Tooltip/UnderlineTooltipDecorator";
import { ReactComponent as SelectXIconSvg } from "icons/redesign/creatives/selectXIcon.svg";

type Props = {
  value?: string | number;
  onChange?: (value: string) => void;
  onBlur?: (value: string) => void;
  label?: string;
  /**
   * Shows the label in full on one line instead of cutting it with an ellipsis.
   * MUI truncates labels at the field width, which is too narrow for wording
   * like "Campaign finish date" in a table cell.
   */
  showFullLabel?: boolean;
  /** Rendered at the far end of the label row, e.g. a "Forgot password?" link. */
  labelAdornment?: React.ReactNode;
  tooltip?: React.ReactNode;
  /**
   * `true` marks the field invalid without printing a message, anything else
   * renders below the field (a node lets callers mix links into the text).
   */
  error?: React.ReactNode;
  helperText?: string;
  warning?: string;
  name?: string;
  type?: string;
  disabled?: boolean;
  readOnly?: boolean;
  multiline?: boolean;
  rows?: number;
  minRows?: number;
  maxRows?: number;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  fullWidth?: boolean;
  placeholder?: string;
  inputProps?: Record<string, unknown>;
  clearable?: boolean;
};

const InputClearIcon = ({
  onClick,
  disabled,
}: {
  onClick: (event: React.MouseEvent<HTMLDivElement>) => void;
  disabled?: boolean;
}) => (
  <div
    onClick={disabled ? undefined : onClick}
    onMouseDown={(event) => event.preventDefault()}
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: 24,
      height: 24,
      borderLeft: `1px solid ${COLORS_TO_BE_REVIEWED.greyBorder}`,
      paddingLeft: 8,
      marginLeft: 8,
      boxSizing: "content-box",
      cursor: disabled ? "default" : "pointer",
      flexShrink: 0,
      visibility: disabled ? "hidden" : "visible",
    }}>
    <SelectXIconSvg width="24px" height="24px" />
  </div>
);

const labelRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  // InputLabel's own bottom margin is dropped when it sits in this row
  marginBottom: 7,
};

const underlineSx = {
  "&:after": {
    borderBottom: commonClasses.inputUnderline.borderBottom,
    borderBottomStyle: "solid",
  },
  "&.MuiInput-root.Mui-disabled:before": {
    borderBottom: commonClasses.inputUnderline.borderBottom,
  },
  "&.Mui-error:before": {
    borderBottomColor: FIGMA_COLORS.errorLight,
  },
};

const Input = ({
  value,
  onChange,
  onBlur,
  label,
  showFullLabel = false,
  labelAdornment,
  tooltip,
  error,
  helperText,
  warning,
  name,
  type = "text",
  disabled,
  readOnly,
  multiline,
  rows,
  minRows,
  maxRows,
  startAdornment,
  endAdornment,
  fullWidth = true,
  placeholder,
  inputProps,
  clearable,
}: Props) => {
  const handleWheel = (event: React.WheelEvent<HTMLInputElement>) => {
    if (type === "number") {
      (event.target as HTMLInputElement).blur();
    }
  };

  // Only strings and elements are printed. Anything else stays swallowed, the
  // way it was before `error` accepted nodes, so existing callers are unaffected.
  const errorMessage =
    typeof error === "string" || isValidElement(error) ? error : null;

  const hasValue = String(value ?? "").length > 0;
  const showClear = clearable && !disabled && !readOnly && hasValue && !endAdornment;

  const handleClear = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    onChange?.("");
  };

  const labelElement = label ? (
    <InputLabel
      error={!!error}
      sx={{
        ...(tooltip ? { pointerEvents: "auto" } : {}),
        ...(labelAdornment ? { marginBottom: 0 } : {}),
        ...(showFullLabel
          ? {
              maxWidth: "none",
              overflow: "visible",
              textOverflow: "clip",
              whiteSpace: "nowrap",
            }
          : {}),
      }}>
      {tooltip ? (
        <UnderlineTooltipDecorator tooltip={tooltip}>
          {label}
        </UnderlineTooltipDecorator>
      ) : label}
    </InputLabel>
  ) : null;

  return (
    <FormControl
      fullWidth={fullWidth}
      disabled={disabled}
      sx={{ "& .MuiInputBase-root": { marginTop: "0px" } }}>
      {labelAdornment ? (
        <div style={labelRowStyle}>
          {labelElement}
          {labelAdornment}
        </div>
      ) : (
        labelElement
      )}
      <MuiInput
        value={value ?? ""}
        type={type}
        error={!!error}
        multiline={multiline}
        rows={rows}
        minRows={minRows}
        maxRows={maxRows}
        startAdornment={startAdornment}
        endAdornment={
          endAdornment ??
          (showClear ? (
            <InputAdornment position="end">
              <InputClearIcon onClick={handleClear} />
            </InputAdornment>
          ) : null)
        }
        placeholder={placeholder}
        onWheel={handleWheel}
        onChange={(e) => onChange?.(e.target.value)}
        onBlur={(e) => onBlur?.(e.target.value)}
        sx={{
          ...commonClasses.inputValue,
          ...underlineSx,
          ...(multiline && showClear
            ? {
                alignItems: "flex-start",
                "& .MuiInputAdornment-root": {
                  alignSelf: "flex-end",
                  marginTop: 0,
                  marginBottom: 0,
                },
              }
            : {}),
        }}
        inputProps={{
          readOnly,
          // Expose a stable test hook derived from `name` (the wrapper does not
          // render `name` to the DOM). An explicit inputProps["data-qa"] wins.
          "data-qa": name ? `input-${name}` : undefined,
          ...inputProps,
        }}
      />
      {errorMessage ? (
        <FormFieldError
          id={name}
          text={errorMessage}
        />
      ) : null}
      {warning && <FormFieldWarning id={name} text={warning} />}
      {helperText && <FormFieldHelperText id={name} text={helperText} />}
    </FormControl>
  );
};

export default Input;
